import { Injectable, Injector } from "@angular/core";
import { NavigationService } from "./navigation.service";
import { NavigationStrategyInterface } from "../interfaces/navigation-strategy.interface";
import { FocusFieldStrategy } from "../directives/navigation-section.directive";
import { BaseEnumPropertyViewModel, BaseEnumTextBoxComponent, DecodeTextBoxComponent, EnumTextBoxComponent, ExternalListComponent, LabelledTextBoxComponent } from "@nts/std";

@Injectable({
    providedIn: 'root'
})
export abstract class BaseNavigationStrategyService implements NavigationStrategyInterface {
    constructor(private injector: Injector) { }

    abstract handleKeyPress(event: KeyboardEvent): void;

    // evito la dipendenza circolare tra i due servizi, richiamandolo tramite l'injector successivamente
    get navigationService(): NavigationService {
        return this.injector.get(NavigationService);
    }

    isFirstOrUniqueFieldOfActiveSection(): boolean {
        const currentMinOrder = this.navigationService.fieldsFromActiveSection.reduce((min, field) => {
            if (field.navigationFieldOrder < min) {
                return field.navigationFieldOrder;
            }

            return min;
        }, Infinity);

        return this.navigationService.activeField.navigationFieldOrder <= currentMinOrder;
    }

    isLastOrUniqueFieldOfActiveSection() {
        const currentMaxOrder = this.navigationService.fieldsFromActiveSection.reduce((max, field) => {
            if (field.navigationFieldOrder > max) {
                return field.navigationFieldOrder;
            }

            return max;
        }, -1);

        return this.navigationService.activeField.navigationFieldOrder >= currentMaxOrder;
    }

    moveToPreviousSection(
        focusFieldStrategy: FocusFieldStrategy = FocusFieldStrategy.Last,
        checkHasLastFocusedField: boolean = true
    ) {
        this.navigationService.setNavigationAction('moveToPreviousSection');
        if (this.navigationService.activeSection != null) {
            const sections = this.navigationService.sectionsFromActiveContainer;

            const currentIndex = sections.findIndex(section =>
                section.sectionId === this.navigationService.activeSection?.sectionId
            );

            if (currentIndex > 0) {
                this.navigationService.activeSection.blur();

                const previousSection = sections[currentIndex - 1];

                if (checkHasLastFocusedField && previousSection.hasLastFocusedField) {
                    previousSection.onFocus(FocusFieldStrategy.RememberLastField);
                } else {
                    previousSection.onFocus(focusFieldStrategy);
                }
            }
        } else {
            if (this.navigationService.previousClosestField) {
                const section = this.navigationService.getSectionFromField(this.navigationService.previousClosestField);

                if (checkHasLastFocusedField && section.hasLastFocusedField) {
                    section.onFocus(FocusFieldStrategy.RememberLastField);
                } else {
                    section.onFocus(focusFieldStrategy);
                }
            } else if (this.navigationService.nextClosestField) {
                const section = this.navigationService.getSectionFromField(this.navigationService.nextClosestField);

                const fields = this.navigationService.getFieldsFromSection(section);

                const currentIndex = fields.findIndex(field =>
                    field.fieldId === this.navigationService.nextClosestField?.fieldId
                );

                if (currentIndex > 0) {
                    const previousField = fields[currentIndex - 1];

                    this.navigationService.setActiveField(previousField.fieldId);
                } else {
                    const allSections = this.navigationService.sectionsFromActiveContainer;

                    const currentIndex = allSections.findIndex(section =>
                        section.sectionId === this.navigationService.nextClosestField?.sectionId
                    );

                    let previousSection = allSections[currentIndex];

                    if (currentIndex > 0) {
                        previousSection = allSections[currentIndex - 1];
                    }

                    if (checkHasLastFocusedField && previousSection.hasLastFocusedField) {
                        previousSection.onFocus(FocusFieldStrategy.RememberLastField);
                    } else {
                        previousSection.onFocus(focusFieldStrategy);
                    }
                }
            }
        }
    }

    moveToPreviousToolbarField(focusFieldStrategy: FocusFieldStrategy = FocusFieldStrategy.First) {
        this.navigationService.setNavigationAction('moveToPreviousToolbarField');
        if (this.navigationService.activeSection?.isToolbar) {
            const success = this.moveToPreviousField(null);
            if (!success && this.navigationService.toolbarSections?.length > 1) {
                const currentIndex = this.navigationService.toolbarSections.findIndex(section =>
                    section.sectionId === this.navigationService.activeSection?.sectionId
                );
                if (currentIndex > 0) {
                    this.navigationService.toolbarSections[currentIndex - 1].onFocus(FocusFieldStrategy.Last);
                } else {
                    this.navigationService.toolbarSections[this.navigationService.toolbarSections.length - 1].onFocus(FocusFieldStrategy.Last);
                }
            }
        } else {
            if (this.navigationService.toolbarSections?.length > 0) {

                if (this.navigationService.activeSection != null) {
                    this.navigationService.activeSection.blur();
                }

                if (this.navigationService.lastToolbarSection) {
                    this.navigationService.lastToolbarSection.onFocus(focusFieldStrategy);
                } else {
                    this.navigationService.toolbarSections[0].onFocus(focusFieldStrategy);
                }
            }
        }
    }

    moveToNextToolbarField(focusFieldStrategy: FocusFieldStrategy = FocusFieldStrategy.First) {
        this.navigationService.setNavigationAction('moveToNextToolbarField');
        if (this.navigationService.activeSection?.isToolbar) {
            const success = this.moveToNextField(null);
            if (success === false && this.navigationService.toolbarSections?.length > 1) {
                const currentIndex = this.navigationService.toolbarSections.findIndex(section =>
                    section.sectionId === this.navigationService.activeSection?.sectionId
                );
                if (currentIndex < this.navigationService.toolbarSections.length - 1) {
                    this.navigationService.toolbarSections[currentIndex + 1].onFocus(FocusFieldStrategy.First);
                } else {
                    this.navigationService.toolbarSections[0].onFocus(FocusFieldStrategy.First);
                }
            }

        } else {
            if (this.navigationService.toolbarSections?.length > 0) {

                if (this.navigationService.activeSection != null) {
                    this.navigationService.activeSection.blur();
                }

                if (this.navigationService.lastToolbarSection) {
                    this.navigationService.lastToolbarSection.onFocus(focusFieldStrategy);
                } else {
                    this.navigationService.toolbarSections[0].onFocus(focusFieldStrategy);
                }
            }
        }
    }

    moveToNextSection(
        focusFieldStrategy: FocusFieldStrategy = FocusFieldStrategy.First,
        checkHasLastFocusedField: boolean = true
    ) {
        this.navigationService.setNavigationAction('moveToNextSection');
        if (this.navigationService.activeSection != null) {
            this.navigationService.activeSection.blur();

            const sections = this.navigationService.sectionsFromActiveContainer;

            const currentIndex = sections.findIndex(section =>
                section.sectionId === this.navigationService.activeSection?.sectionId
            );

            if (currentIndex < sections.length - 1) {
                const nextSection = sections[currentIndex + 1];

                if (checkHasLastFocusedField && nextSection.hasLastFocusedField) {
                    nextSection.onFocus(FocusFieldStrategy.RememberLastField);
                } else {
                    nextSection.onFocus(focusFieldStrategy);
                }
            }
        } else {
            if (this.navigationService.previousClosestField) {
                const section = this.navigationService.getSectionFromField(this.navigationService.previousClosestField);

                if (checkHasLastFocusedField && section.hasLastFocusedField) {
                    section.onFocus(FocusFieldStrategy.RememberLastField);
                } else {
                    section.onFocus(focusFieldStrategy);
                }
            } else if (this.navigationService.nextClosestField) {
                const section = this.navigationService.getSectionFromField(this.navigationService.nextClosestField);

                const fields = this.navigationService.getFieldsFromSection(section);

                const currentIndex = fields.findIndex(field => field.fieldId === this.navigationService.nextClosestField?.fieldId);

                if (checkHasLastFocusedField && section.hasLastFocusedField) {
                    section.onFocus(FocusFieldStrategy.RememberLastField);
                } else {
                    section.onFocus(focusFieldStrategy);
                }
            }
        }
    }

    /**
     * Sposta il focus al campo precedente della sezione attiva.
     * Se non c'è un campo attivo, prova a spostare il focus all'ultimo campo della sezione precedente.
     * Se non c'è una sezione precedente, non fa nulla.
     * @param event KeyboardEvent che ha generato lo spostamento
     * @returns     True se il focus è stato spostato, false altrimenti, stop se è stato forzato lo stop.
     */
    moveToPreviousField(event: KeyboardEvent): boolean | 'stop' {
        this.navigationService.setNavigationAction('moveToPreviousField');
        if (this.navigationService.activeField != null) {
            if (
                this.stopNavigationOnEnumComponent(event) ||
                this.stopNavigationOnDecodeTextBoxComponent(event) ||
                this.stopNavigationOnExternalListComponent(event)
            ) {
                return 'stop';
            }

            this.navigationService.activeField.blur();

            const fields = this.navigationService.fieldsFromActiveSection;

            const currentIndex = fields.findIndex(field => field.fieldId === this.navigationService.activeField?.fieldId);

            if (currentIndex > 0) {
                const previousField = fields[currentIndex - 1];
                previousField.onFocus();
                return true;
            }
        } else {
            if (this.navigationService.previousClosestField) {
                // console.log('moveToPreviosField previousClosestField');
            } else if (this.navigationService.nextClosestField) {
                const section = this.navigationService.getSectionFromField(this.navigationService.nextClosestField);

                const currentIndex = this.navigationService.sectionsFromActiveContainer.findIndex(section =>
                    section.sectionId === this.navigationService.nextClosestField?.sectionId
                );

                if (currentIndex === 0) {
                    const fields = this.navigationService.getFieldsFromSection(section);

                    if (fields?.length > 0) {
                        fields[fields?.length - 1].onFocus();
                        return true;
                    }
                } else {
                    const fields = this.navigationService.getFieldsFromSection(
                        this.navigationService.sectionsFromActiveContainer[currentIndex - 1]
                    );

                    if (fields?.length > 0) {
                        fields[fields?.length - 1].onFocus();
                        return true;
                    }
                }
            }
        }
        return false;
    }

    /**
     * Sposta il focus al campo successivo della sezione attiva.
     * Se non c'è un campo attivo, prova a spostare il focus al primo campo della sezione successiva.
     * Se non c'è una sezione successiva, non fa nulla.
     * @param event KeyboardEvent che ha generato lo spostamento
     * @returns     True se il focus è stato spostato, false altrimenti, stop se è stato forzato lo stop.
     */
    moveToNextField(event: KeyboardEvent): boolean | 'stop' {
        this.navigationService.setNavigationAction('moveToNextField');
        if (this.navigationService.activeField != null) {
            if (
                this.stopNavigationOnEnumComponent(event) ||
                this.stopNavigationOnDecodeTextBoxComponent(event) ||
                this.stopNavigationOnExternalListComponent(event)
            ) {
                return 'stop';
            }

            this.navigationService.activeField.blur();

            const fields = this.navigationService.fieldsFromActiveSection;
            const currentIndex = fields.findIndex(field => field.fieldId === this.navigationService.activeField?.fieldId);

            if (currentIndex < fields.length - 1) {
                const nextField = fields[currentIndex + 1];
                nextField.onFocus();
                return true;
            }
        } else {
            if (this.navigationService.previousClosestField) {
                console.log('moveToNextField previousClosestField');
            } else if (this.navigationService.nextClosestField) {
                const section = this.navigationService.getSectionFromField(this.navigationService.nextClosestField);
                const fields = this.navigationService.getFieldsFromSection(section);

                if (fields?.length > 0) {
                    fields[0].onFocus();
                    return true;
                }
            }
        }
        return false;
    }

    stopNavigationOnEnumComponent(event: KeyboardEvent): boolean {
        if (this.navigationService.activeField?.typedComponent instanceof LabelledTextBoxComponent) {
            const pvm = this.navigationService.activeField?.typedComponent?.propertyViewModel;

            if (
                pvm instanceof BaseEnumPropertyViewModel
                && (
                    event.code === "ArrowDown"
                    || event.code === "ArrowUp"
                )
            ) {
                // Se il dropdown è aperto, non voglio che il focus vada al campo successivo
                // Se il dropdown è chiuso, voglio che il focus vada al campo successivo
                if (
                    pvm.componentIstance$.value instanceof LabelledTextBoxComponent
                    && pvm.componentIstance$.value.enumTextBoxComponent$.value instanceof EnumTextBoxComponent
                ) {
                    if (pvm.componentIstance$.value.enumTextBoxComponent$.value.baseEnumTextBoxComponent$.value.combo.overlayVisible === true) {
                        return true;
                    }
                }

                if (
                    pvm.componentIstance$.value instanceof EnumTextBoxComponent
                    && pvm.componentIstance$.value.baseEnumTextBoxComponent$.value.combo.overlayVisible
                ) {
                    return true;
                }
            }
        }

        if (this.navigationService.activeField?.typedComponent instanceof BaseEnumTextBoxComponent) {
            if (
                (
                    event.code === "ArrowDown"
                    || event.code === "ArrowUp"
                )
            ) {
                // Se il dropdown è aperto, non voglio che il focus vada al campo successivo
                // Se il dropdown è chiuso, voglio che il focus vada al campo successivo
                if ((this.navigationService.activeField?.typedComponent as BaseEnumTextBoxComponent).combo.overlayVisible === true) {
                    return true;
                }
            }
        }

        return false;
    }

    stopNavigationOnExternalListComponent(event: KeyboardEvent): boolean {
        if (this.navigationService.activeField?.typedComponent instanceof ExternalListComponent) {
            if (event.code === "ArrowDown" || event.code === "ArrowUp") {
                // Se il dropdown è aperto, non voglio che il focus vada al campo successivo
                // Se il dropdown è chiuso, voglio che il focus vada al campo successivo
                if (this.navigationService.activeField?.typedComponent.textBox?.baseExternalBox?.isOpen$.value) {
                    return true;
                }
            }
        }
        return false;
    }

    stopNavigationOnDecodeTextBoxComponent(event: KeyboardEvent): boolean {
        if (this.navigationService.activeField?.typedComponent instanceof DecodeTextBoxComponent) {
            if (event.code === "ArrowDown" || event.code === "ArrowUp") {
                // Se il dropdown è aperto, non voglio che il focus vada al campo successivo
                // Se il dropdown è chiuso, voglio che il focus vada al campo successivo
                if (this.navigationService.activeField?.typedComponent.textBox?.baseExternalBox?.isOpen$.value) {
                    return true;
                }
            }
        }
        return false;
    }
}
